import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Pencil, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const STAGES = [
  { id: "new", label: "New", tint: "border-l-[color:var(--accent-warm)]" },
  { id: "contacted", label: "Contacted", tint: "border-l-amber-400" },
  { id: "qualified", label: "Qualified", tint: "border-l-[color:var(--primary)]" },
  { id: "converted", label: "Converted", tint: "border-l-green-500" },
  { id: "lost", label: "Lost", tint: "border-l-red-400" },
];

const SOURCES = ["instagram", "facebook", "google", "website", "referral", "walk_in", "other"];

export default function Leads() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [leads, setLeads] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/leads");
      setLeads(data);
    } catch (err) {
      console.error("Failed to load leads:", err);
      toast.error("Failed to load leads");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const updateStage = async (lead, stage) => {
    try {
      await api.put(`/leads/${lead.id}`, { ...lead, stage });
      toast.success("Stage updated");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} lead(s)? This cannot be undone.`)) return;
    try {
      const { data } = await api.post("/leads/bulk-delete", { ids: [...selected] });
      toast.success(`Deleted ${data.deleted} lead(s)`);
      setSelected(new Set());
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Bulk delete failed"); }
  };

  return (
    <div data-testid="leads-page">
      <div className="mb-7 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[color:var(--muted)]">Marketing CRM</p>
          <h1 className="font-heading text-3xl md:text-4xl text-[color:var(--ink)] font-medium mt-1">Leads pipeline</h1>
        </div>
        {isAdmin && selected.size > 0 && (
          <div className="flex items-center gap-3 text-sm" data-testid="leads-bulk-bar">
            <span className="font-medium text-[color:var(--ink)]">{selected.size} selected</span>
            <button onClick={() => setSelected(new Set())} className="text-xs text-[color:var(--muted)] hover:text-[color:var(--ink)] underline-offset-2 hover:underline">Clear</button>
            <Button variant="outline" size="sm" onClick={bulkDelete} className="rounded-full text-xs h-8 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700" data-testid="leads-bulk-delete">
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete selected
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {STAGES.map((stage) => {
          const filtered = leads.filter((l) => l.stage === stage.id);
          return (
            <div key={stage.id} className="bg-[color:var(--bg-marketing)] rounded-2xl p-3 min-h-[200px]" data-testid={`stage-${stage.id}`}>
              <div className="flex items-center justify-between px-2 py-1 mb-3">
                <h3 className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">{stage.label}</h3>
                <span className="text-xs font-medium text-[color:var(--ink)]">{filtered.length}</span>
              </div>
              <div className="space-y-2.5">
                {filtered.map((l) => (
                  <div key={l.id} className={`bg-white border border-[color:var(--border)] border-l-4 ${stage.tint} rounded-xl p-3.5 ${selected.has(l.id) ? "ring-2 ring-[color:var(--primary)] ring-offset-1" : ""}`} data-testid={`lead-card-${l.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-medium text-[color:var(--ink)] text-sm truncate">{l.name}</p>
                        <p className="text-xs text-[color:var(--muted)] mt-0.5 truncate">{l.condition || "—"}</p>
                      </div>
                      {isAdmin && (
                        <div className="flex items-center gap-1">
                          <button onClick={() => setEditing(l)} className="text-[color:var(--muted)] hover:text-[color:var(--primary)]" data-testid={`lead-edit-${l.id}`} aria-label="Edit lead">
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <Checkbox checked={selected.has(l.id)} onCheckedChange={() => toggleOne(l.id)} aria-label="Select lead" data-testid={`lead-select-${l.id}`} />
                        </div>
                      )}
                    </div>
                    <div className="mt-2.5 flex items-center justify-between gap-2">
                      <span className="text-[10px] tracking-wider uppercase px-2 py-0.5 rounded-full bg-[color:var(--primary-light)]/40 text-[color:var(--primary-hover)]">{l.source}</span>
                      <Select value={l.stage} onValueChange={(v) => updateStage(l, v)}>
                        <SelectTrigger className="h-7 text-xs w-[110px]" data-testid={`lead-stage-${l.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {STAGES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    {l.phone && <p className="text-xs text-[color:var(--muted)] mt-2">{l.phone}</p>}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <LeadEditDialog
          existing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function LeadEditDialog({ existing, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: existing.name || "",
    phone: existing.phone || "",
    email: existing.email || "",
    condition: existing.condition || "",
    source: existing.source || "website",
    stage: existing.stage || "new",
    message: existing.message || "",
  });
  const [loading, setLoading] = useState(false);

  const save = async () => {
    if (!form.name) { toast.error("Name is required"); return; }
    setLoading(true);
    try {
      await api.put(`/leads/${existing.id}`, form);
      toast.success("Lead updated");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="lead-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Edit lead</DialogTitle>
          <DialogDescription className="text-[color:var(--muted)]">Update any field — including stage and source.</DialogDescription>
        </DialogHeader>
        <div className="grid md:grid-cols-2 gap-4 mt-2">
          <F k="name" l="Name *" form={form} setForm={setForm} />
          <F k="phone" l="Phone" form={form} setForm={setForm} />
          <F k="email" l="Email" form={form} setForm={setForm} type="email" />
          <F k="condition" l="Condition" form={form} setForm={setForm} />
          <div>
            <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">Source</label>
            <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
              <SelectTrigger className="mt-1.5" data-testid="lead-source"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SOURCES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">Stage</label>
            <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
              <SelectTrigger className="mt-1.5" data-testid="lead-stage-edit"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STAGES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">Message / notes</label>
            <Textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5" data-testid="lead-message" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="lead-cancel">Cancel</Button>
          <Button onClick={save} disabled={loading} className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white" data-testid="lead-save">{loading ? "Saving…" : "Save changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function F({ k, l, form, setForm, type = "text" }) {
  return (
    <div>
      <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">{l}</label>
      <Input type={type} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1.5" data-testid={`lead-${k}`} />
    </div>
  );
}
